import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  IconButton,
  Skeleton,
  Theme,
  Typography,
  useTheme,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';
import {
  CheckedIcon,
  DialogCloseIcon,
  UncheckedIcon,
} from '../../../assets/SvgIcons';
import { getSelfDeclarationDocument } from '../actions';
import {
  selfDeclarationDocumentBody,
  getSelfDeclarationDocumentResponseBody,
  SubbrokerDeclarationDocType,
} from '../types';

interface Props {
  open: boolean;
  documentType: SubbrokerDeclarationDocType | string;
  onCancel: () => void;
  onAccept: (version: string) => void;
}

export default function DocumentDialog({
  open,
  documentType,
  onCancel,
  onAccept,
}: Props): JSX.Element {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [doc, setDoc] = useState<selfDeclarationDocumentBody | null>(null);
  const [acknowledged, setAcknowledged] = useState<boolean>(false);
  const [isFetching, setIsFetching] = useState<boolean>(false);

  useEffect(() => {
    if (!open) return;
    setDoc(null);
    setAcknowledged(false);

    const fetchDocument = async () => {
      try {
        setIsFetching(true);
        const res = (await dispatch(
          getSelfDeclarationDocument({ documentType })
        )) as unknown as getSelfDeclarationDocumentResponseBody;
        if (!res || !res.isSuccess) {
          throw new Error(res?.message || 'Failed to load document');
        }
        setDoc(res.data);
      } catch (e: any) {
        enqueueSnackbar(e?.message || 'Failed to load document', {
          variant: 'error',
          autoHideDuration: 3000,
        });
      } finally {
        setIsFetching(false);
      }
    };

    fetchDocument();
  }, [open, documentType, dispatch, enqueueSnackbar]);

  const handleSubmit = () => {
    if (!doc?.version) return;
    onAccept(doc.version);
  };

  if (!open) return <></>;

  return (
    <Dialog
      onClose={onCancel}
      open={open}
      fullWidth
      sx={{
        '& .MuiPaper-root': {
          borderRadius: '15px',
          backgroundColor: 'background.default',
          maxWidth: '900px',
          padding: '25px',
          m: 0,
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h2" sx={{ fontWeight: 600 }}>
            {isFetching ? (
              <Skeleton variant="text" width={320} />
            ) : (
              doc?.title || 'Declaration'
            )}
          </Typography>
          <IconButton onClick={onCancel} aria-label="Close dialog">
            <DialogCloseIcon />
          </IconButton>
        </Box>

        <Box
          sx={{
            mt: 3,
            maxHeight: '50vh',
            overflowY: 'auto',
            pr: 1,
          }}
        >
          {isFetching ? (
            <>
              <Skeleton variant="text" />
              <Skeleton variant="text" />
              <Skeleton variant="text" width="85%" />
              <Skeleton variant="text" />
              <Skeleton variant="text" width="70%" />
            </>
          ) : (
            <Typography
              variant="body_500"
              sx={{
                whiteSpace: 'pre-line',
                color: theme.palette.text.primary,
              }}
            >
              {doc?.body}
            </Typography>
          )}
        </Box>

        {!isFetching && doc?.checkBoxText && (
          <Box sx={{ mt: 3 }}>
            <FormControlLabel
              sx={{
                alignItems: 'flex-start',
                ml: 0,
                mr: 0,
                '& .MuiCheckbox-root': {
                  p: 0,
                  mt: '2px',
                  mr: 1.25,
                },
              }}
              control={
                <Checkbox
                  checked={acknowledged}
                  onChange={(e) => setAcknowledged(e.target.checked)}
                  checkedIcon={<CheckedIcon />}
                  icon={<UncheckedIcon />}
                />
              }
              label={
                <Typography variant="body_500">{doc.checkBoxText}</Typography>
              }
            />
          </Box>
        )}

        <DialogActions sx={{ width: '100%', mt: 3, p: 0 }}>
          <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={onCancel}
              sx={(theme: Theme) => ({
                color: 'primary.500',
                backgroundColor: 'transparent',
                borderColor: 'primary.500',
                borderWidth: '0.5px',
                borderStyle: 'solid',
                padding: '10px',
                '&:hover, &.Mui-focusVisible': {
                  borderColor: 'primary.600',
                  backgroundColor: 'background.default',
                },
                ...theme.typography.body_600,
              })}
            >
              Cancel
            </Button>
            <Button
              fullWidth
              variant="contained"
              disabled={!acknowledged || isFetching}
              onClick={handleSubmit}
              sx={(theme: Theme) => ({
                padding: '10px',
                ...theme.typography.body_600,
              })}
            >
              Submit
            </Button>
          </Box>
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
}
